import { FC, FormEvent, useId } from "react";
import { Box, Dialog, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Button } from "src/domains/shared/components/Button";
import { ModalHeader } from "src/domains/shared/components/ModalHeader";
import { COLOR_CONSTANTS } from "src/domains/shared/constants/styles";
import { BusyLabel, HOVER_TRANSITION_MS, REDUCED_MOTION_SX } from "src/domains/shared/components/StateTransition";
import {
  MYPAGE_DIALOG_ACTION_MIN_WIDTH_PX,
  MYPAGE_DIALOG_ACTIONS_SX,
  MYPAGE_DIALOG_PAPER_SX,
  MYPAGE_EMAIL_PLACEHOLDER,
  myPageEmailInputSx,
} from "../myPageStyles";

interface AddMemberDialogProps {
  open: boolean;
  email: string;
  errorText: string | null;
  adding: boolean;
  onEmailChange: (value: string) => void;
  onSubmit: () => void;
  onClose: () => void;
}

/**
 * 멤버 추가 창 — 멤버 삭제 창과 같은 규격(폭·여백·취소/실행 버튼 배치, `myPageStyles` 의 창 상수)을 쓴다.
 * 오류 문구 자리를 미리 잡아 둬 오류가 나도 창 크기가 그대로다.
 */
export const AddMemberDialog: FC<AddMemberDialogProps> = ({
  open,
  email,
  errorText,
  adding,
  onEmailChange,
  onSubmit,
  onClose,
}) => {
  const { t } = useTranslation("projects");
  const titleId = useId();
  const errorId = useId();

  // 추가 중에는 버튼이 스피너를 보여야 하므로 비활성으로 흐리게 두지 않는다.
  const isSubmitBlocked = (!email.trim() || Boolean(errorText)) && !adding;

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!adding) onSubmit();
  };

  return (
    <Dialog
      open={open}
      onClose={adding ? undefined : onClose}
      aria-labelledby={titleId}
      PaperProps={{ sx: MYPAGE_DIALOG_PAPER_SX }}
    >
      <ModalHeader title={t("projectPermission.addMember", { ns: "projects" })} titleId={titleId} size="small" onClose={() => !adding && onClose()} />
      <Box component="form" noValidate onSubmit={handleSubmit}>
        {/* 아래 여백은 오류 문구 자리(4 + 16px)가 맡는다 — 삭제 확인 창과 높이를 맞춘다. */}
        <Box sx={{ padding: "14px 20px 0" }}>
          <Box
            component="input"
            type="email"
            autoComplete="off"
            autoFocus
            aria-label={t("myPageModal.permission.emailLabel", { ns: "projects" })}
            aria-invalid={errorText ? true : undefined}
            aria-describedby={errorText ? errorId : undefined}
            placeholder={MYPAGE_EMAIL_PLACEHOLDER}
            value={email}
            disabled={adding}
            onChange={(event) => onEmailChange((event.target as HTMLInputElement).value)}
            sx={myPageEmailInputSx(Boolean(errorText))}
          />
          <Typography
            id={errorId}
            role={errorText ? "alert" : undefined}
            sx={{
              height: 16,
              marginTop: "4px",
              fontSize: 11,
              lineHeight: "16px",
              color: COLOR_CONSTANTS.error.main,
              opacity: errorText ? 1 : 0,
              transition: `opacity ${HOVER_TRANSITION_MS}ms ease`,
              ...REDUCED_MOTION_SX,
            }}
          >
            {errorText}
          </Typography>
        </Box>
        <Box sx={MYPAGE_DIALOG_ACTIONS_SX}>
          <Button type="button" size="small" emphasis="outlined" intent="inherit" disabled={adding} onClick={onClose}>
            {t("constants.cancel", { ns: "common" })}
          </Button>
          <Button
            type="submit"
            size="small"
            emphasis="contained"
            intent="primary"
            disabled={isSubmitBlocked}
            sx={{ minWidth: MYPAGE_DIALOG_ACTION_MIN_WIDTH_PX }}
          >
            <BusyLabel
              label={t("myPage.permission.actions.add", { ns: "projects" })}
              busy={adding}
              busyLabel={t("myPageModal.permission.adding", { ns: "projects" })}
              spinnerSize={12}
            />
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};
